/**
 * Client-side masking of obviously credential-shaped text in captured trace
 * payloads.
 *
 * THIS IS DEFENCE IN DEPTH, NOT A CONTROL. The payloads are raw HTTP bodies and
 * headers captured by the log service; the authoritative redaction belongs
 * there, because anything this function misses has already crossed the network
 * and is sitting in the Apollo cache regardless. What it buys is that the two
 * highest-frequency leaks — a bearer token and a cookie header pasted verbatim
 * into a shared screenshot — do not render on screen by default.
 *
 * It is deliberately narrow. Matching on key NAMES and the `Bearer` prefix has
 * a knowable false-positive cost (a field genuinely called `token` that carries
 * no secret is shown as `***`), whereas entropy-style heuristics would blank
 * arbitrary identifiers an operator needs in order to debug. When in doubt this
 * shows the value, because the panel already sits behind a diagnostic-access
 * gate and an over-masked payload is a useless one.
 */

export const REDACTED = '***'

const SENSITIVE_KEY = [
  'authorization',
  'proxy-authorization',
  'set-cookie',
  'cookie',
  'password',
  'passwd',
  'secret',
  'api[-_]?key',
  'access[-_]?token',
  'refresh[-_]?token',
  'id[-_]?token',
  'session[-_]?(?:id|token)'
].join('|')

/** `"authorization": "…"` in a JSON body, including escaped quotes. */
const JSON_PAIR = new RegExp(
  `("(?:${SENSITIVE_KEY})"\\s*:\\s*)"(?:[^"\\\\]|\\\\.)*"`,
  'gi'
)

/** `Authorization: …` as a header line. */
const HEADER_LINE = new RegExp(
  `^([ \\t]*(?:${SENSITIVE_KEY})[ \\t]*:[ \\t]*).+$`,
  'gim'
)

/** A bearer token anywhere — query strings and form bodies included. */
const BEARER = /\b(bearer\s+)[A-Za-z0-9\-._~+/]+=*/gi

export function maskTracePayload(payload: string): string {
  if (!payload) return payload
  return payload
    .replace(JSON_PAIR, `$1"${REDACTED}"`)
    .replace(HEADER_LINE, `$1${REDACTED}`)
    .replace(BEARER, `$1${REDACTED}`)
}
